import AdBid from './bidsResource';

import bids from '@/api/shared/resource/bids';
import transformBids from '@/api/shared/transformers/bids/bidsTransformer';
import errorTransformer from '@/api/shared/transformers/errorTransformer';
import campaignIdAndRangeNormalizer from '@/api/shared/normalizers/campaignIdAndRangeNormalizer';
import transformToCamelCase from '../shared/transformers/toCamelCaseTransformer';
import transformCSV from '../shared/transformers/CSVTransformer';

import translator from '@/locales';

/**
 * Gets bids
 *
 * @async
 * @param {Object}
 * @returns {Promise} Promise object represents an array with bids data
 */
function getBids({ campaignId, range }) {
  return AdBid.getBids(campaignIdAndRangeNormalizer(campaignId, range))
    .then(({ body: { metadata, bids } }) => ({
      metadata: transformToCamelCase(metadata),
      bids: transformBids(bids),
    }))
    .catch(errorTransformer);
}

/**
 * Changes multiple bids statuses
 *
 * @async
 * @param {object} info
 * @param {string} info.campaignId
 * @param {number[]} info.ids
 * @param {bool} info.paused
 * @returns {Promise} message
 */
function changeBidsStatus({ campaignId, ids, paused }) {
  return editBids({
    campaignId,
    bidsInfo: ids.map(id => ({ id, paused })),
  });
}

/**
 * Changes single bid status
 *
 * @async
 * @param {object} info
 * @param {string} info.campaignId
 * @param {number} info.id
 * @param {bool} info.paused
 * @returns {Promise} message
 */
function changeSingleBidStatus({ campaignId, id, paused }) {
  return editBids({
    campaignId,
    bidsInfo: [
      {
        id,
        paused,
      },
    ],
  });
}

/**
 * Changes single bid's matching type
 *
 * @async
 * @param {object} info
 * @param {string} info.campaignId
 * @param {number} info.id
 * @param {bool} info.matchingType
 * @returns {Promise} message
 */
function changeMatchingType({ campaignId, id, matchingType }) {
  return editBids({
    campaignId,
    bidsInfo: [
      {
        id,
        matchingType,
      },
    ],
  });
}

/**
 *
 * @typedef {Object} bidInfo
 * @property {number} id
 * @property {string} matchingType
 * @property {number} priceOffer
 */
/**
 * Edits bid's data
 *
 * @async
 * @param {Object} info
 * @param {string} info.campaignId
 * @param {Array<bidInfo>} info.bidsInfo
 */
function editBids({ campaignId, bidsInfo }) {
  return AdBid.editBids(
    { itemId: campaignId },
    {
      data: bidsInfo,
    },
  )
    .then(
      ({
        body: { message = translator.t('ADVERTISEMENT.BID.EDIT_SUCCESS') },
      }) => message,
    )
    .catch(errorTransformer);
}

/**
 *
 * @typedef {Object} bidInfo
 * @property {number} id
 * @property {string} matchingType
 * @property {number} priceOffer
 */
/**
 * Edits single bid's data
 *
 * @async
 * @param {string} campaignId
 * @param {bidInfo} bidInfo
 */
function editBid(campaignId, bidInfo) {
  return editBids({ campaignId, bidsInfo: [bidInfo] });
}

/**
 * Deletes bid
 *
 * @async
 * @param {object} bidInfo
 * @param {Array<number>} bidInfo.ids
 * @param {string} bidInfo.campaignId
 * @returns {Promise} Promise object with a message on resolve
 */
function deleteBids({ ids, campaignId }) {
  return AdBid.deleteBids(
    {
      itemId: campaignId,
    },
    { data: ids.map(id => ({ id })) },
  ).catch(errorTransformer);
}

/**
 * Gets bids' suggestions list
 *
 * @async
 * @param {string} campaignId
 * @returns {Promise<string[]>}
 */

function getSuggestions(campaignId) {
  return AdBid.getQuerySuggestions({ itemId: campaignId })
    .then(({ body: { suggestions = [] } }) => {
      return suggestions;
    })
    .catch(errorTransformer);
}

/**
 * Rejects bids' suggestion
 *
 * @async
 * @param {object} params
 * @param {string} params.campaignId
 * @param {string} params.suggestion
 * @returns {Promise<{ message: string }>}
 */

function rejectSuggestion({ campaignId, suggestion }) {
  return AdBid.rejectSuggestion(
    { itemId: campaignId },
    {
      query: suggestion,
    },
  )
    .then(({ body: { message = '' } }) => {
      return message;
    })
    .catch(errorTransformer);
}

function addBid({ fromSuggestions = false, ...params }) {
  const referrer = fromSuggestions ? 'suggestion' : null;
  return bids.addBid({
    ...params,
    referrer,
  });
}

function getBidsCSV({ campaignId, range }) {
  return AdBid.getBidsCSV(campaignIdAndRangeNormalizer(campaignId, range))
    .then(res => transformCSV(res))
    .catch(errorTransformer);
}

export default {
  getBids,
  deleteBids,
  addBid,
  editBids,
  rejectSuggestion,
  getSuggestions,
  changeBidsStatus,
  changeSingleBidStatus,
  changeMatchingType,
  editBid,
  getBidsCSV,
};
